import { useEffect, useRef, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { api, track } from "@/lib/api";
import { PostCard, SkeletonCard } from "./PostCard";
import { EmptyState, CreateButtons } from "./EmptyState";
import { ArrowUp } from "lucide-react";
import { CategoryIcon } from "./ui";

export const CategoryTiles = ({ value, onChange }) => {
  const { meta, L } = useApp();
  return (
    <div className="flex gap-3 overflow-x-auto no-scrollbar snap-x pb-1 -mx-4 px-4" data-testid="category-tiles">
      {meta.categories.map((c) => (
        <button key={c.id} data-testid={`category-tile-${c.id}`} aria-pressed={value === c.id} onClick={() => onChange(value === c.id ? "" : c.id)}
          className={`press snap-start shrink-0 relative w-32 h-40 rounded-[20px] overflow-hidden text-left ${value === c.id ? "ring-4 ring-[#F59E0B]" : ""}`}>
          {c.tile_image ? <img src={c.tile_image.replace("w=900", "w=320")} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-[#1E4D3B] ankara-dots" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#133E2F] via-[#133E2F]/40 to-transparent" />
          <div className="absolute bottom-0 p-3 text-white">
            <CategoryIcon icon={c.icon} className="w-5 h-5 text-[#F59E0B] mb-1" />
            <div className="font-bold text-sm leading-tight">{L(c)}</div>
          </div>
        </button>
      ))}
    </div>
  );
};

const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} aria-pressed={active} onClick={onClick}
    className={`press shrink-0 min-h-[40px] px-4 rounded-full text-sm font-semibold border ${active ? "bg-[#1E4D3B] text-white border-[#1E4D3B]" : "bg-[#FFFDF9] border-[#E8E2D9] text-[#291E18]"}`}>{children}</button>
);

export const FilterBar = ({ f, set }) => {
  const { t, meta, L } = useApp();
  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4" data-testid="category-chips">
        <Chip testid="chip-all-categories" active={!f.category} onClick={() => set({ category: "" })}>{t("all")}</Chip>
        {meta.categories.map((c) => <Chip key={c.id} testid={`chip-${c.id}`} active={f.category === c.id} onClick={() => set({ category: c.id })}>{L(c)}</Chip>)}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex rounded-full bg-[#F3EDE4] p-1" role="group">
          {[["", "all"], ["listing", "listings"], ["request", "requests"]].map(([v, k]) => (
            <button key={k} data-testid={`type-filter-${k}`} aria-pressed={f.type === v} onClick={() => set({ type: v })}
              className={`press min-h-[40px] px-4 rounded-full text-sm font-bold ${f.type === v ? (v === "request" ? "bg-[#C2410C] text-white" : "bg-[#1E4D3B] text-white") : "text-[#6B5E55]"}`}>{t(k)}</button>
          ))}
        </div>
        <div className="flex rounded-full bg-[#F3EDE4] p-1" role="group">
          {[["active", "active_only"], ["all", "all_statuses"]].map(([v, k]) => (
            <button key={v} data-testid={`status-filter-${v}`} aria-pressed={f.status === v} onClick={() => set({ status: v })}
              className={`press min-h-[40px] px-4 rounded-full text-sm font-bold ${f.status === v ? "bg-[#FFFDF9] text-[#291E18] shadow" : "text-[#6B5E55]"}`}>{t(k)}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const Feed = ({ hub, filters }) => {
  const { t, L, cat } = useApp();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const sentinel = useRef(null);
  const key = JSON.stringify({ hub: hub?.id, ...filters });

  const load = useCallback(async (pg) => {
    setLoading(true);
    const params = { hub: hub?.id, page: pg, limit: 12, ...filters };
    Object.keys(params).forEach((k) => !params[k] && delete params[k]);
    try {
      const { data } = await api.get("/posts", { params });
      setItems((prev) => (pg === 1 ? data.items : [...prev, ...data.items]));
      setHasMore(data.has_more); setPage(pg);
    } finally { setLoading(false); }
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setItems([]); load(1); }, [load]);
  useEffect(() => {
    if (filters.category || filters.type || filters.area || filters.side) track("filter_used", { hub_id: hub?.id, category_id: filters.category || null, meta: filters });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const [fresh, setFresh] = useState([]);
  const top = items[0]?.created_at;
  useEffect(() => { setFresh([]); }, [key]);
  useEffect(() => {
    if (!top) return;
    const id = setInterval(async () => {
      if (document.hidden) return;
      const params = { hub: hub?.id, limit: 30, ...filters, since: fresh[0]?.created_at || top };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      try {
        const { data } = await api.get("/posts", { params });
        if (!data.items.length) return;
        const incoming = [...data.items, ...fresh];
        if (window.scrollY < 400) {
          setItems((prev) => [...incoming, ...prev.filter((p) => !incoming.some((n) => n.id === p.id))]);
          setFresh([]);
        } else setFresh(incoming);
      } catch { /* offline */ }
    }, 30000);
    return () => clearInterval(id);
  }, [top, key, fresh]); // eslint-disable-line react-hooks/exhaustive-deps
  const showFresh = () => {
    setItems((prev) => [...fresh, ...prev.filter((p) => !fresh.some((n) => n.id === p.id))]); setFresh([]);
    const el = sentinel.current?.parentElement; if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 140, behavior: "smooth" });
  };

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting && !loading) load(page + 1); }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, page, load]);

  const city = L(hub);
  const emptyTitle = filters.q ? t("empty_search", { city }) : filters.category ? t("empty_category", { cat: L(cat(filters.category)), city })
    : filters.type === "request" ? t("empty_requests", { city }) : t("empty_listings", { city });

  return (
    <div>
      {fresh.length > 0 && (
        <button data-testid="new-posts-pill" onClick={showFresh} className="press rise fixed top-20 left-1/2 -translate-x-1/2 z-40 min-h-[44px] px-5 rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold shadow-xl inline-flex items-center gap-2">
          <ArrowUp className="w-4 h-4" />{t("new_posts", { n: fresh.length })}
        </button>
      )}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="feed-grid">
        {items.map((p, i) => <div key={p.id} className="rise" style={{ animationDelay: `${Math.min(i % 12, 6) * 40}ms` }}><PostCard p={p} /></div>)}
        {loading && Array.from({ length: items.length ? 3 : 6 }).map((_, i) => <SkeletonCard key={`s${i}`} />)}
      </div>
      {!loading && items.length === 0 && <EmptyState title={emptyTitle}><CreateButtons /></EmptyState>}
      <div ref={sentinel} className="h-4" />
    </div>
  );
};
